import _, { isArray } from "lodash";
import { NavigateFunction } from "react-router-dom";
import parse, { domToReact, HTMLReactParserOptions, Element as HtmlElement } from 'html-react-parser';
import { MODAL_ANIMATION } from "../animations/animations";
import {
  baseFontSize,
  contactFormFields,
  ERRORS,
  MODAL_TYPE,
  SCREEN_BREAKPOINTS,
  selectedEnquiryOptions,
  SPECIAL_WORDS
} from "../constants/appConstants";
import { InternalAxiosRequestConfig } from "axios";

declare const window: WindowWithDataLayer;
interface SubpageHeaderConfig {
  maxHeightTop?: number;
  maxHeightBottom?: number;
  mobileBreakpoint?: number;
}

interface OptionItem {
  id: number;
  attributes: {
    OptionName: string;
    enquiry_category: {
      data: {
        id: number;
        attributes: {
          CategoryName: string;
          Order: string;
        };
      };
    };
  };
}

interface DropdownItem {
  id: number | string;
  label: string;
}

interface HeaderHeights {
  maxHeightTop: number;
  maxHeightBottom: number;
}

export interface RouteMenuItem {
  label: string;
  path: string;
  onClick: () => void;
}

export interface Route {
  Label: string;
  Path: string;
}

export const getBrandMenuItems = (
  ourBrands: any,
  setIsMenuOpen?: (val: boolean) => void
): any => {
  return ourBrands
    .map((brand: any) => {
      const rawUrl = brand?.attributes?.WebsiteURL;

      let validatedUrl: string;
      try {
        validatedUrl = new URL(rawUrl).toString();
      } catch {
        return null;
      }

      return {
        label: brand?.attributes?.BrandName,
        path: validatedUrl,
        order: brand?.attributes?.Order,
        onClick: () => {
          window.open(validatedUrl, "_blank");
          setIsMenuOpen?.(false);
        },
      };
    })
    .filter(Boolean);
};

export const getRouteMenuItems = (
  routeItems: Route[],
  navigate: NavigateFunction,
  setIsMenuOpen: (open: boolean) => void
): RouteMenuItem[] => {
  return routeItems.map((route) => ({
    label: route?.Label,
    path: route?.Path,
    onClick: () => {
      const normalizedPath = (
        route?.Path || "/"
      ).replace(/^\/+/, "/");

      navigate(normalizedPath);
      setIsMenuOpen(false);
    },
  }));
};

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const formatJobLocation = (city?: string, region?: string): string => {
  const normalizedCity = city?.trim() ? capitalize(city.trim()) : "";
  const normalizedRegion = region?.trim() || "";

  return [normalizedCity, normalizedRegion].filter(Boolean).join(", ");
};

// utils/scrollbarUtils.ts

export const calculateThumbStyle = (
  container: HTMLElement,
  thumb: HTMLElement,
  trackInset = 100,
  minThumbHeight = 30
) => {
  const { scrollTop, scrollHeight, clientHeight } = container;

  // Match: scrollTrackHeight = clientHeight - 100
  const scrollTrackHeight = clientHeight - trackInset;

  if (scrollHeight <= clientHeight) {
    thumb.style.display = "none";
    return;
  }

  thumb.style.display = "block";

  const thumbHeight = Math.max(
    (clientHeight / scrollHeight) * scrollTrackHeight,
    minThumbHeight
  );

  const thumbTop =
    (scrollTop / (scrollHeight - clientHeight)) *
    (scrollTrackHeight - thumbHeight);

  thumb.style.height = `${thumbHeight}px`;
  thumb.style.transform = `translateY(${thumbTop}px)`;
};

export const attachScrollThumb = (
  scrollEl: HTMLElement | null,
  thumbEl: HTMLElement | null,
  inset = 100,
  minThumbHeight = 30
): (() => void) | void => {
  if (!scrollEl || !thumbEl) return;

  const handleScroll = () => {
    calculateThumbStyle(scrollEl, thumbEl, inset, minThumbHeight);
  };

  scrollEl.addEventListener("scroll", handleScroll);
  handleScroll(); // run once immediately

  // Return a cleanup function to remove the listener
  return () => {
    scrollEl.removeEventListener("scroll", handleScroll);
  };
};

export function extractYoutubeVideoId(url: string): string | null {
  if (!url) return null;

  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([^\s&?/]+)/
  );

  return match?.[1] || null;
}

export const safeArray = (value: any) => {
  return isArray(value) ? value : [];
};

export const getVisibleSections = (sections: any[]): any[] => {
  return sections.filter((section) => section.sectionVisible);
};

export const sortByOrder = (array: any[] = []) => {
  return array.slice().sort((a, b) => {
    const orderA = a?.attributes?.Order;
    const orderB = b?.attributes?.Order;

    if (orderA == null && orderB == null) return 0;
    if (orderA == null) return 1;
    if (orderB == null) return -1;

    return orderA - orderB;
  });
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidName = (name: string): boolean => {
  const nameRegex = /^[a-zA-Z\s]{1,50}$/;
  return nameRegex.test(name);
};

export const validateNewsletterForm = (formData: {
  name: string;
  email: string;
}) => {
  const errors = {
    name: "",
    email: "",
  };

  if (_.isEmpty(formData.name)) {
    errors.name = ERRORS.REQUIRED_FIELD;
  } else if (!isValidName(formData.name)) {
    errors.name = ERRORS.VALID_NAME;
  }

  if (_.isEmpty(formData.email)) {
    errors.email = ERRORS.REQUIRED_FIELD;
  } else if (!isValidEmail(formData.email)) {
    errors.email = ERRORS.VALID_EMAIL;
  }

  const hasError = !_.isEmpty(errors.name) || !_.isEmpty(errors.email);

  return {
    errors,
    isValid: !hasError,
  };
};

export const validateContactForm = (
  formData: any,
  setFieldErrors: (
    field: string,
    errorMessage: string,
    isFails: boolean
  ) => void
): boolean => {
  let isValid = true;

  const requiredFields = getRequiredFields(formData);

  for (const [field, required] of Object.entries(requiredFields)) {
    const value = formData[field]?.trim?.() ?? formData[field];

    if (required && !value) {
      setFieldErrors(field, ERRORS.REQUIRED_FIELD, true);
      isValid = false;
      continue;
    }

    // field-specific validations
    if (field === contactFormFields.EMAIL && value) {
      if (!isValidEmail(value)) {
        setFieldErrors(field, ERRORS.VALID_EMAIL, true);
        isValid = false;
        continue;
      }
    }

    if (field === contactFormFields.PHONE && value) {
      if (value.length < 8) {
        // or use MIN_PHONE_NUMBER_LENGTH
        setFieldErrors(field, ERRORS.VALID_PHONE, true);
        isValid = false;
        continue;
      }
    }

    // clear error if all good
    setFieldErrors(field, "", false);
  }

  return isValid;
};

export const getRequiredFields = (formData: any) => ({
  [contactFormFields.FULL_NAME]: true,
  [contactFormFields.EMAIL]: true,
  [contactFormFields.PHONE]: true,
  [contactFormFields.ENQUIRY]: true,
  [contactFormFields.VIN]:
    formData[contactFormFields.ENQUIRY] ===
    selectedEnquiryOptions.SELL_YOUR_CAR,
});

export function sortByOrderBrand<T extends { order?: number }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}

export const formatDateToLong = (dateString: string) => {

  if (!dateString) return "";

  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const getCloseButtonConfig = (closeButtonType: any, styles: any) => {
  switch (closeButtonType) {
    case MODAL_TYPE.SECONDARY:
      return {
        color: MODAL_ANIMATION.secondary,
        className: styles.closeButtonSecondary,
      };
    default:
      return {
        color: MODAL_ANIMATION.default,
        className: styles.closeButtonPrimary,
      };
  }
};

export const getValidatedUrl = (
  rawUrl: string | undefined | null
): string | null => {
  if (!rawUrl) return null;
  try {
    return new URL(rawUrl).toString();
  } catch (_) {
    return null;
  }
};

export const calculateScrollBasedValues = (
  scrollY: number,
  config: {
    maxScroll?: number;
    minHeight?: number;
    maxHeight?: number;
    logoWidthMin?: number;
    logoWidthMax?: number;
    mobileBreakpoint?: number;
  } = {}
) => {
  const {
    maxScroll = 400,
    minHeight = 80,
    maxHeight = 100,
    logoWidthMin = 120,
    logoWidthMax = 160,
    mobileBreakpoint = SCREEN_BREAKPOINTS.SMALL,
  } = config;

  if (typeof window !== "undefined" && window.innerWidth <= mobileBreakpoint) {
    return {
      scrollPercent: 0,
      headerHeight: pxToRem(56),
      logoWidth: pxToRem(109),
      logoHeight: pxToRem(46),
    };
  }

  const scrollPercent = Math.min(scrollY, maxScroll) / maxScroll;
  const headerHeightPx = maxHeight - (maxHeight - minHeight) * scrollPercent;
  const logoWidthPx =
    logoWidthMax - (logoWidthMax - logoWidthMin) * scrollPercent;
  const logoHeightPx = logoWidthPx * (55 / 164);

  return {
    scrollPercent,
    headerHeight: pxToRem(headerHeightPx),
    logoWidth: pxToRem(logoWidthPx),
    logoHeight: pxToRem(logoHeightPx),
  };
};

export const calculateSubpageHeaderHeight = (
  config: SubpageHeaderConfig = {}
): HeaderHeights => {
  const {
    maxHeightTop = 64,
    maxHeightBottom = 60,
    mobileBreakpoint = SCREEN_BREAKPOINTS.SMALL,
  } = config;

  const isMobile =
    typeof window !== "undefined" && window.innerWidth <= mobileBreakpoint;

  return isMobile
    ? { maxHeightTop: 87, maxHeightBottom: 48 }
    : { maxHeightTop, maxHeightBottom };
};

export const getDrawerOverflowStyle = (
  brandItemsLength: number,
  windowWidth: number = typeof window !== "undefined" ? window.innerWidth : 0
): "scroll" | "hidden" => {
  const isMediumScreen =
    windowWidth >= SCREEN_BREAKPOINTS.SMALL &&
    windowWidth <= SCREEN_BREAKPOINTS.MEDIUM;
  const isLargeScreen =
    windowWidth > SCREEN_BREAKPOINTS.MEDIUM &&
    windowWidth <= SCREEN_BREAKPOINTS.LARGE;

  if (
    (isMediumScreen && brandItemsLength >= 11) ||
    (isLargeScreen && brandItemsLength >= 12)
  ) {
    return "scroll";
  }
  return "scroll";
};

export function calculateTotalWidth(
  baseWidth: number,
  baseItems: number,
  totalItems: number,
  extraWidthPerItem: number
): number {
  const extraItems = totalItems > baseItems ? totalItems - baseItems : 0;
  return baseWidth + extraItems * extraWidthPerItem;
}

export function pxToRem(px: number, base: number = baseFontSize): string {
  return `${px / base}rem`;
}

export const remToPxScroll = (rem: number) => {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
};

export const getRoleNames = (roleNamesData: any): string => {
  if (!roleNamesData?.data || !Array.isArray(roleNamesData.data)) {
    return "--";
  }

  return roleNamesData.data
    .map((r: any) => r?.attributes?.RoleName)
    .filter(Boolean)
    .join(" / ");
};

export const groupOptionsByCategory = (options: OptionItem[]) => {
  if (!options) return [];

  // Group by category id
  const grouped = options.reduce((acc: any, option: OptionItem) => {
    const cat = option.attributes.enquiry_category?.data;
    if (!cat) return acc;

    const catId = cat.id;
    if (!acc[catId]) {
      acc[catId] = {
        order: parseInt(cat.attributes.Order, 10) || 999,
        options: [],
      };
    }
    acc[catId].options.push(option);
    return acc;
  }, {});

  // Convert to array & sort categories by order
  const sorted = Object.entries(grouped)
    .sort(([, a]: any, [, b]: any) => a.order - b.order)
    .map(([categoryId, data]: any) => ({
      categoryId,
      options: data.options,
    }));

  return sorted;
};

export const normalizeOptions = (
  items: any[],
  labelKey: string,
  valueKey: string = "id"
) => {
  if (!items || !items.length) return [];
  return items.map((item) => ({
    id: item[valueKey] ?? item.id,
    label: item[labelKey] ?? "",
  }));
};

export const truncateText = (text: string, maxLength: number) => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

//

export const getIsActive = (
  isOpen: boolean,
  selectedLabel?: string,
  selectedOption?: DropdownItem | null
): boolean => {
  return (
    isOpen || Boolean(selectedLabel && selectedOption && selectedOption.id)
  );
};

export const getSelectedLabel = (
  selectedOption: DropdownItem | null | undefined,
  truncate?: boolean,
  truncateLimit: number = 20
): string => {
  if (!selectedOption || !selectedOption.id) return "";
  if (truncate && selectedOption.label)
    return truncateText(selectedOption.label, truncateLimit);
  return selectedOption.label || "";
};

export const getOptionsWithPlaceholder = (
  options: DropdownItem[],
  required: boolean,
  placeholder: DropdownItem,
  defaultLabel: string
): DropdownItem[] => {
  if (required) return options;

  if (options.length === 0) return options;

  // Only add placeholder if first option's label is not the default
  if (options[0].label !== defaultLabel) {
    return [placeholder, ...options];
  }

  return options;
};

export const getSelectedOption = (
  options: DropdownItem[],
  value: string | number | undefined,
  returnLabel: boolean = false
): DropdownItem | undefined => {
  // Try to find match based on returnLabel
  const match = options.find((opt) =>
    returnLabel ? opt.label === String(value) : opt.id === String(value)
  );

  if (match) return match;

  // Fallback: match by id as string
  return options.find((opt) => String(opt.id) === String(value));
};

export const dropDownNormalizeOptions = (
  options: any[],
  getOptionValue: (opt: any) => number | string,
  getOptionLabel: (opt: any) => string,
  defaultLabel: string
): DropdownItem[] => {
  if (options && options.length > 0) {
    return options.map((opt) => ({
      id: getOptionValue(opt),
      label: getOptionLabel(opt),
    }));
  }
  return [{ id: "", label: defaultLabel }];
};

export const isDropdownItem = (value: any): value is DropdownItem => {
  return (
    value && typeof value === "object" && "id" in value && "label" in value
  );
};

export const splitLines = (text: any) => text?.split("\n") || [];

export const formatNumberWithCommas = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = value.toString().replace(/[^\d]/g, "");
  if (numeric === "") return "";
  return Number(numeric).toLocaleString();
};

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
  gtag: any;
};

export const triggerCustomEventOnGA = (eventName: any, eventLabel: any = '') => {
  console.log(eventName, "eventName");
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      event_category: 'DGR FORM',
      event_label: eventLabel,
    });
  } else {
    console.warn('gtag not defined');
  }
};

export const formatSpecialHeading = (text: string) => {
  if (!text) return text;

  const escapedWords = SPECIAL_WORDS.map((word) =>
    word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );

  const regex = new RegExp(`(${escapedWords.join("|")})`, "gi");

  return text.split(regex).map((part, index) => {
    const matchedWord = SPECIAL_WORDS.find(
      (word) => word.toLowerCase() === part.toLowerCase()
    );

    if (matchedWord) {
      return (
        <span key={index} className="brandGroup">
          <span className="brandLine">{matchedWord}</span>
        </span>
      );
    }

    return <span key={index}>{part}</span>;
  });
};

export const isExternalLink = (url: string): boolean => {
  if (!url) return false;

  try {
    const link = new URL(url, window.location.origin);

    return link.origin !== window.location.origin;
  } catch {
    return false;
  }
};

export const isValidLink = (url: string): boolean => {
  if (!url?.trim()) return false;

  try {
    new URL(url, window.location.origin);

    return true;
  } catch {
    return false;
  }
};

export const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const reorderCountries = (topCountries: string[]) => {
  const list = document.querySelector(
    ".country-list"
  ) as HTMLUListElement | null;
  if (!list) return;
  const items = Array.from(list.children) as HTMLLIElement[];
  const searchItem = items.find((li) => li.classList.contains("search"));
  const divider = items.find((li) => li.classList.contains("divider"));
  const countryItems = items.filter((li) => li.classList.contains("country"));
  const orderedTop = topCountries
    .map((code) => countryItems.find((li) => li.dataset.countryCode === code))
    .filter(Boolean) as HTMLLIElement[];
  orderedTop.forEach((li) => li.remove());
  let anchor: Element | null = searchItem ?? null;
  orderedTop.forEach((li) => {
    if (anchor) {
      anchor.after(li);
      anchor = li;
    } else {
      list.prepend(li);
      anchor = li;
    }
  });
  const remainingCountries = countryItems.filter(
    (li) => !topCountries.includes(li.dataset.countryCode!)
  );
  const getDialCode = (li: HTMLLIElement) => {
    const span = li.querySelector(".dial-code");
    if (!span) return 0;
    return Number(span.textContent?.replace(/\D/g, "") || 0);
  };
  remainingCountries.sort((a, b) => getDialCode(a) - getDialCode(b));
  let insertAfter: Element | null = divider ?? anchor;
  remainingCountries.forEach((li) => {
    if (insertAfter) {
      insertAfter.after(li);
      insertAfter = li;
    } else {
      list.prepend(li);
      insertAfter = li;
    }
  });
};

export const isToggleEnabled = (value: any): boolean => {
  return value === true || value === 1 || value === "true" || value === "1";
};

export function parseHtmlWithLinksInNewTab(html: string | null | undefined) {
  if (!html) return null;

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof HtmlElement && domNode.name === 'a' && domNode.attribs?.href) {
        const href = domNode.attribs.href;
        if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          return;
        }
        return (
          <a {...domNode.attribs} target="_blank" rel="noopener noreferrer">
            {domToReact(domNode.children as any, options)}
          </a>
        );
      }
    },
  };

  return parse(html, options);
}

export const resolveRequestPath = (cfg: InternalAxiosRequestConfig): string => {
  const base = cfg.baseURL ?? "";
  const url = cfg.url ?? "";
  const full = new URL(url, base || window.location.origin);
  return full.pathname;
};